import { FC } from 'react'
import MarketChart from './MarketChart'
import MetricDetails from './MetricDetails'
import { DataProcessor } from '../services/data-processor'
import { useBreakpoint } from '../hooks/use-breakpoint'
import { HousingMarketResponse } from '@libs/types/generated/housing-market-ui'

interface Props {
  geo: string
  propertyType: string
  marketData: HousingMarketResponse
}

const Metrics: FC<Props> = ({ geo, propertyType, marketData }) => {
  const { md } = useBreakpoint()
  const chartHeight = md ? 360 : 210

  const processor = new DataProcessor(marketData)
  const metrics = processor.metricsByPropertyType(propertyType)

  const scoreboards = {
    home_inventory: metrics?.home_inventory?.scoreboards?.[0],
    homes_sold: metrics?.homes_sold?.scoreboards?.[0],
    median_days_on_market: metrics?.median_days_on_market?.scoreboards?.[0],
    median_home_values: metrics?.median_home_values?.scoreboards?.[0]
  }

  return (
    <div>
      <div className="display-grid grid-cols-1 md:grid-cols-2 gap-12 my-6">
        <div className="col-span-1">
          <div className="text-lg font-bold mb-2 ml-2">
            Trended Median Home Values
          </div>
          <MarketChart
            chartId="median_home_values"
            format="currency"
            dataPoints={metrics?.median_home_values?.dataset || []}
            chartHeight={chartHeight}
          />
        </div>
        <div className="col-span-1 md:pt-16">
          <MetricDetails
            title={`How much do homes in ${geo} cost?`}
            desc={metrics?.median_home_values?.content}
            metricLabel={scoreboards.median_home_values?.title}
            metricValue={scoreboards.median_home_values?.content?.[0]}
            trend={scoreboards.median_home_values?.icon}
            trendText={scoreboards.median_home_values?.description}
          />
        </div>
      </div>

      <div className="border-t border-gray mx-auto my-8 md:my-16 hm-wrapper-md"></div>

      <div className="display-grid grid-cols-1 md:grid-cols-2 gap-12 my-6">
        <div className="col-span-1">
          <div className="text-lg font-bold mb-2 ml-2">
            Trended Homes Inventory
          </div>
          <MarketChart
            chartId="home_inventory"
            dataPoints={metrics?.home_inventory?.dataset || []}
            chartHeight={chartHeight}
          />
        </div>
        <div className="col-span-1 md:pt-16">
          <MetricDetails
            title={`How many homes are available in ${geo}?`}
            desc={metrics?.home_inventory?.content}
            metricLabel={scoreboards.home_inventory?.title}
            metricValue={scoreboards.home_inventory?.content?.[0]}
            trend={scoreboards.home_inventory?.icon}
            trendText={scoreboards.home_inventory?.description}
          />
        </div>
      </div>

      <div className="border-t border-gray mx-auto my-8 md:my-16 hm-wrapper-md"></div>

      <div className="display-grid grid-cols-1 md:grid-cols-2 gap-12 my-6">
        <div className="col-span-1">
          <div className="text-lg font-bold mb-2 ml-2">Trended Homes Sold</div>
          <MarketChart
            chartId="homes_sold"
            dataPoints={metrics?.homes_sold?.dataset || []}
            chartHeight={chartHeight}
          />
        </div>
        <div className="col-span-1 md:pt-16">
          <MetricDetails
            title={`How many homes are sold in ${geo}?`}
            desc={metrics?.homes_sold?.content}
            metricLabel={scoreboards.homes_sold?.title}
            metricValue={scoreboards.homes_sold?.content?.[0]}
            trend={scoreboards.homes_sold?.icon}
            trendText={scoreboards.homes_sold?.description}
          />
        </div>
      </div>

      <div className="border-t border-gray mx-auto my-8 md:my-16 hm-wrapper-md"></div>

      <div className="display-grid grid-cols-1 md:grid-cols-2 gap-12 my-6">
        <div className="col-span-1">
          <div className="text-lg font-bold mb-2 ml-2">
            Trended Median Days on Market
          </div>
          <MarketChart
            chartId="test2"
            dataPoints={metrics?.median_days_on_market?.dataset || []}
            chartHeight={chartHeight}
          />
        </div>
        <div className="col-span-1 md:pt-16">
          <MetricDetails
            title={`How long does it take to sell a home in ${geo}?`}
            desc={metrics?.median_days_on_market?.content}
            metricLabel={scoreboards.median_days_on_market?.title}
            metricValue={scoreboards.median_days_on_market?.content?.[0]}
            trend={scoreboards.median_days_on_market?.icon}
            trendText={scoreboards.median_days_on_market?.description}
          />
        </div>
      </div>
    </div>
  )
}

export default Metrics
